import { useEffect, useMemo, useState, useCallback } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import { GET_FACETS } from '@/graphql/queries';
import { flyerboardActiveFiltersVar, flyerboardAllFiltersVar } from '@/graphql/state';
import { Facets, GetFacetsResponse } from '@/graphql/types';

import { FlyerType } from '@/config/constants';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { FlyerCategories } from '@/types/flyer';

import { useAnalytics } from '@/hooks/useAnalytics';
import { useChildGrades } from '@/hooks/useChildGrades';

import { useIsMobile } from '@/hooks';
import { DrawerFilter } from './DrawerFilter';
import { ModalFilter } from './ModalFilter';
import { getGradeRange } from '@/utils/getGradeRange';
import { useGoogleTranslator, useCategories } from '@/hooks';

interface FiltersProps {
  trackId: string;
  flyerType: string;
  showFilter: boolean;
  setShowFilter: (showFilter: boolean) => void;
  trackingEventFrom?: string;
}

type OptionType = {
  value: string;
  description: string;
  disabled?: boolean;
};

export const FlyerboardFilters: React.FC<FiltersProps> = ({
  trackId,
  flyerType,
  showFilter,
  setShowFilter,
  trackingEventFrom,
}) => {
  const { search } = useLocation();
  const isMobile = useIsMobile();
  const searchParams = new URLSearchParams(search);
  const categoryId = searchParams.get('categoryId');
  const flyerboardActiveFilters = useReactiveVar(flyerboardActiveFiltersVar);
  const flyerboardAllFilters = useReactiveVar(flyerboardAllFiltersVar);

  const viewResultsTranslatedLabel = useGoogleTranslator(`View # results`);

  const { lowGrade, highGrade, grades } = useChildGrades();
  const { schoolCategories, communityCategories } = useCategories();

  const [gradeFilter, setGradeFilter] = useState<{ from?: string; to?: string }>({
    from: lowGrade,
    to: highGrade,
  });

  useEffect(() => {
    setGradeFilter({ from: lowGrade, to: highGrade });
  }, [lowGrade, highGrade]);

  const handleGradeChange = useCallback((field: 'from' | 'to', value?: string) => {
    setGradeFilter(prev => ({ ...prev, [field]: value }));
  }, []);

  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const { track, trackEvent } = useAnalytics();

  const queryInput = useMemo(() => {
    const filters = { ...flyerboardAllFilters };
    delete filters.categories;
    delete filters.grades;

    if (categoryId) {
      filters.categories = [categoryId];
    } else if (selectedTypes.length > 0) {
      filters.categories = [...selectedTypes];
    }

    if (gradeFilter.from && gradeFilter.to) {
      const grades = getGradeRange(gradeFilter.from, gradeFilter.to).map(g => `${g.id}`);
      filters.grades = grades;
    }
    filters.totalsOnly = true;

    return { filters };
  }, [flyerboardAllFilters, selectedTypes, categoryId, gradeFilter]);

  const {
    data,
    previousData,
    loading: facetsLoading,
  } = useQuery<GetFacetsResponse>(GET_FACETS, {
    variables: { input: queryInput },
    skip: !flyerboardAllFilters,
  });

  const facets = data?.getFacets.facets ?? previousData?.getFacets.facets;
  const amount = data?.getFacets.pagination.total ?? previousData?.getFacets.pagination.total ?? 0;

  const persistFilters = () => {
    localStorage.setItem('filtersUpdateAt', new Date().toString());
    localStorage.setItem('selectedTypes', JSON.stringify(selectedTypes));
    localStorage.setItem('activeFlyerboard', window.location.href);
  };

  const onSelectTypesToggle = useCallback(
    (value: string, description: string) => () => {
      setSelectedTypes(prev => {
        const updated = [...prev];
        const index = updated.indexOf(value);

        track({
          action: GenericTrackTypes.CHECKBOX_SELECTED,
          category: `${trackId}:filter:types:${index === -1 ? 'select' : 'unselect'}`,
          label: description,
          value: Number(value),
        });

        if (index === -1) {
          updated.push(value);
        } else {
          updated.splice(index, 1);
        }

        updated.sort();
        return updated;
      });
    },
    [track, trackId]
  );

  const clearLocalFilters = () => {
    setSelectedTypes([]);
    setGradeFilter({ from: lowGrade, to: highGrade });
  };

  const onClearFilters = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:clear_filters`,
      label: 'Clear All',
    });
    clearLocalFilters();

    flyerboardActiveFiltersVar({ types: [], grades: [] });

    flyerboardAllFiltersVar({
      ...(flyerboardAllFilters ?? {}),
      categories: undefined,
      grades: undefined,
      schoolIds: flyerboardAllFilters?.schoolIds ?? [],
    });
  };

  const onShowResults = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:show_results`,
      label: 'Show Results',
    });
    persistFilters();

    flyerboardActiveFiltersVar({ types: selectedTypes, grades: queryInput.filters?.grades ?? [] });

    const trackingData = {
      stage: TrackingStage.discovery,
      event: TrackingEvents.filterFlyerSet,
      event_detail_from: trackingEventFrom,
      stage_from: TrackingStage.discovery,
      event_from: TrackingEvents.viewFlyerSet,
    };

    if (gradeFilter.from && gradeFilter.to) {
      trackEvent({
        ...trackingData,
        event_detail: EventDetails.filterFlyerSet('grades'),
      });
    }
    if (selectedTypes.length > 0) {
      trackEvent({
        ...trackingData,
        event_detail: EventDetails.filterFlyerSet('types'),
      });
    }

    setShowFilter(false);
  };

  const onToggleDrawer = (state: boolean) => setShowFilter(state);

  useEffect(() => {
    if (showFilter) {
      setSelectedTypes(flyerboardActiveFilters.types ?? []);
    }
  }, [showFilter, flyerboardActiveFilters]);

  const disableClearAllButton = useMemo(
    () =>
      gradeFilter.from === lowGrade && gradeFilter.to === highGrade && selectedTypes.length === 0,
    [gradeFilter, lowGrade, highGrade, selectedTypes]
  );

  const viewResultsLabel = useMemo(
    () => viewResultsTranslatedLabel.replace('#', amount.toString()),
    [amount]
  );

  const filterProps = {
    isOpen: showFilter,
    title: `${flyerType} Filters`,
    viewResultsLabel,
    onClearFilters,
    onShowResults,
    onToggle: onToggleDrawer,
    selectedTypes,
    disableClearAllButton,
    flyerType,
    onSelectTypesToggle,
    types:
      categoryId || !facets
        ? []
        : getCategories(flyerType, facets, schoolCategories, communityCategories),
    grades,
    gradeFilter,
    initialGradeValue: { from: lowGrade, to: highGrade },
    handleGradeChange,
    facetsLoading,
  };

  return isMobile ? <DrawerFilter {...filterProps} /> : <ModalFilter {...filterProps} />;
};

function getCategories(
  flyerType: string,
  facets: Facets,
  schoolCategories: FlyerCategories,
  communityCategories: FlyerCategories
): OptionType[] {
  const categories = flyerType === FlyerType.school ? schoolCategories : communityCategories;
  return categories.map(category => ({
    description: category.title,
    value: category.id,
    disabled: false,
  }));
}
